import type { ChecklistStatus } from './utils';

const MINIMUM_FUZZY_MATCH_LENGTH = 3;

export const SETUP_EVIDENCE_LABELS = {
  accountName: 'Account name',
  propertyName: 'Property name',
  propertyWebsite: 'Property website',
  propertyCountry: 'Property country',
  abnVerified: 'ABN verified',
  abnStatus: 'ABN status',
  abn: 'ABN',
  hotelAccountCurrency: 'Hotel account currency',
  opportunityCurrency: 'Opportunity currency',
  excludedBrands: 'Excluded brands',
  excludedCountries: 'Excluded countries',
  loadedScheduleRows: 'Loaded schedule rows',
  canonicalPropertyChain: 'Canonical property chain',
  contractTemplateId: 'Contract template ID',
  leContractsGeo: 'LE Contracts Geo',
  sellCurrency: 'Sell currency',
  costCurrency: 'Cost currency',
  reportingCurrency: 'Reporting currency',
  packageCurrencies: 'Package currencies',
} as const;

export interface ChecklistEvidence {
  canOverride?: boolean;
  error?: boolean;
  label: string;
  value: string;
}

export interface SetupChecklistRow {
  category: string;
  evidence: Array<ChecklistEvidence>;
  field: string;
  id: string;
  rule: string;
  status: ChecklistStatus;
  statusLabel: string;
  summary: string;
}

export interface CampaignSchedule {
  brand?: string | null;
  market?: string | null;
  region?: string | null;
}

export interface SetupRuleInput {
  account?: {
    abn?: string | null;
    abnStatus?: string | null;
    abnVerified?: boolean | string | null;
    bankCurrency?: string | null;
    contractTemplateId?: string | null;
    currencyIsoCode?: string | null;
    name?: string | null;
  };
  campaignSchedules?: Array<CampaignSchedule>;
  chainName?: string | null;
  exclusions?: {
    brands?: Array<string> | string | null;
    countries?: Array<string> | string | null;
  };
  opportunity?: {
    costPriceCurrency?: string | null;
    currencyIsoCode?: string | null;
    geo?: string | null;
  };
  property?: {
    country?: string | null;
    name?: string | null;
    website?: string | null;
  };
  packageCurrencies?: Array<string | null | undefined>;
  sourceErrors?: {
    account?: string;
    chain?: string;
    opportunity?: string;
    property?: string;
  };
}

function normalise(value?: string | null) {
  return (value ?? '').trim().toLowerCase();
}

function formatValue(value?: string | number | boolean | null) {
  if (value === undefined || value === null || value === '') {
    return '-';
  }

  return String(value);
}

function formatEvidence(
  value: string | number | boolean | null | undefined,
  error?: string,
): Omit<ChecklistEvidence, 'label'> {
  return error ? { error: true, value: error } : { value: formatValue(value) };
}

function toList(value?: Array<string> | string | null) {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  if (!value) {
    return [];
  }

  return value
    .split(/[,;]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function isTrue(value?: boolean | string | null) {
  if (typeof value === 'boolean') {
    return value;
  }

  return ['true', 'yes', 'y', '1'].includes(normalise(value));
}

function statusFromCheck(check: boolean): ChecklistStatus {
  return check ? 'approved' : 'requires approval';
}

function hasValue(value?: string | null) {
  return !!normalise(value);
}

function uniqueValues(values: Array<string | null | undefined>) {
  return Array.from(new Set(values.map((value) => normalise(value)).filter(Boolean)));
}

function valuesMatch(left?: string | null, right?: string | null) {
  const normalisedLeft = normalise(left);
  const normalisedRight = normalise(right);
  const allowFuzzyMatch =
    normalisedLeft.length >= MINIMUM_FUZZY_MATCH_LENGTH && normalisedRight.length >= MINIMUM_FUZZY_MATCH_LENGTH;

  return (
    !!normalisedLeft &&
    !!normalisedRight &&
    (normalisedLeft === normalisedRight ||
      (allowFuzzyMatch && (normalisedLeft.includes(normalisedRight) || normalisedRight.includes(normalisedLeft))))
  );
}

function schedulesExcludeSelections(
  schedules: Array<CampaignSchedule> | undefined,
  excludedBrands: Array<string>,
  excludedCountries: Array<string>,
) {
  if (!schedules?.length) {
    return false;
  }

  const selectedBrands = new Set(excludedBrands.map(normalise));
  const selectedCountries = new Set(excludedCountries.map(normalise));

  return schedules.every((schedule) => {
    const scheduleBrand = normalise(schedule.brand);
    const scheduleMarket = normalise(schedule.market ?? schedule.region);

    return !selectedBrands.has(scheduleBrand) && !selectedCountries.has(scheduleMarket);
  });
}

function getAccountNameSummary(
  hasAccountNameError: boolean,
  accountName?: string | null,
  propertyName?: string | null,
) {
  if (hasAccountNameError) {
    return 'Required account or property evidence could not be loaded.';
  }

  if (hasValue(accountName) && hasValue(propertyName)) {
    return 'Account and property names were compared.';
  }

  return 'Account or property name is missing.';
}

function getAbnSummary(hasAbnError: boolean, hasPropertyCountry: boolean, isAustralia: boolean) {
  if (hasAbnError) {
    return 'Required account or property evidence could not be loaded.';
  }

  if (!hasPropertyCountry) {
    return 'Property country is missing.';
  }

  return isAustralia
    ? 'Australian account ABN verification was checked.'
    : 'ABN verification is not required outside Australia.';
}

function getCampaignScheduleSummary(
  hasCampaignExclusionsError: boolean,
  hasExclusions: boolean,
  campaignScheduleCount: number,
) {
  if (hasCampaignExclusionsError) {
    return 'Required opportunity evidence could not be loaded.';
  }

  if (!hasExclusions) {
    return 'Brand and market exclusion evidence is not loaded.';
  }

  if (campaignScheduleCount === 0) {
    return 'Campaign schedule rows are not loaded.';
  }

  return 'Campaign schedules were checked against selected exclusions.';
}

export function buildSetupChecklistRules(input: SetupRuleInput): Array<SetupChecklistRow> {
  const excludedBrands = toList(input.exclusions?.brands);
  const excludedCountries = toList(input.exclusions?.countries);
  const sellCurrency = input.opportunity?.currencyIsoCode;
  const costCurrency = input.opportunity?.costPriceCurrency;
  const packageCurrencies = uniqueValues(input.packageCurrencies ?? []);
  const reportingCurrency = input.account?.bankCurrency ?? input.account?.currencyIsoCode;
  const propertyCountry = normalise(input.property?.country);
  const isAustralia = propertyCountry === 'australia' || propertyCountry === 'au';
  const hasPropertyCountry = hasValue(input.property?.country);
  const sourceErrors = input.sourceErrors ?? {};
  const hasAccountNameError = !!sourceErrors.account || !!sourceErrors.property;
  const hasAbnError = !!sourceErrors.account || !!sourceErrors.property;
  const hasAccountCurrencyError = !!sourceErrors.account || !!sourceErrors.opportunity;
  const hasCampaignExclusionsError = !!sourceErrors.opportunity;
  const hasMarketingTermsError = !!sourceErrors.chain || !!sourceErrors.account;
  const hasGeoError = !!sourceErrors.opportunity || !!sourceErrors.account;
  const hasCurrencyError = !!sourceErrors.opportunity || !!sourceErrors.account;

  return [
    {
      id: 'setup-account-name',
      category: 'Setup',
      field: 'Account name',
      rule: 'Account name should match the hotel name on their website.',
      status: statusFromCheck(
        !hasAccountNameError &&
          hasValue(input.account?.name) &&
          hasValue(input.property?.name) &&
          normalise(input.account?.name) === normalise(input.property?.name),
      ),
      statusLabel: 'Account name',
      summary: getAccountNameSummary(hasAccountNameError, input.account?.name, input.property?.name),
      evidence: [
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.accountName,
          ...formatEvidence(input.account?.name, sourceErrors.account),
        },
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.propertyName,
          ...formatEvidence(input.property?.name, sourceErrors.property),
        },
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.propertyWebsite,
          ...formatEvidence(input.property?.website, sourceErrors.property),
        },
      ],
    },
    {
      id: 'setup-account-abn',
      category: 'Setup',
      field: 'ABN',
      rule: 'Australia: ABN Verified must be ticked.',
      status: statusFromCheck(
        !hasAbnError && hasPropertyCountry && (!isAustralia || isTrue(input.account?.abnVerified)),
      ),
      statusLabel: 'ABN',
      summary: getAbnSummary(hasAbnError, hasPropertyCountry, isAustralia),
      evidence: [
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.propertyCountry,
          ...formatEvidence(input.property?.country, sourceErrors.property),
        },
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.abnVerified,
          ...formatEvidence(input.account?.abnVerified, sourceErrors.account),
        },
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.abnStatus,
          ...formatEvidence(input.account?.abnStatus, sourceErrors.account),
        },
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.abn,
          ...formatEvidence(input.account?.abn, sourceErrors.account),
        },
      ],
    },
    {
      id: 'setup-account-currency',
      category: 'Setup',
      field: 'Hotel account currency',
      rule: 'Hotel account currency should match the opportunity currency.',
      status: statusFromCheck(
        !hasAccountCurrencyError &&
          hasValue(input.account?.bankCurrency ?? input.account?.currencyIsoCode) &&
          hasValue(input.opportunity?.currencyIsoCode) &&
          normalise(input.account?.bankCurrency ?? input.account?.currencyIsoCode) ===
            normalise(input.opportunity?.currencyIsoCode),
      ),
      statusLabel: 'Hotel account currency',
      summary: hasAccountCurrencyError
        ? 'Required account or opportunity evidence could not be loaded.'
        : 'Hotel account currency was compared with opportunity currency.',
      evidence: [
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.hotelAccountCurrency,
          ...formatEvidence(input.account?.bankCurrency ?? input.account?.currencyIsoCode, sourceErrors.account),
        },
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.opportunityCurrency,
          ...formatEvidence(input.opportunity?.currencyIsoCode, sourceErrors.opportunity),
        },
      ],
    },
    {
      id: 'setup-campaign-schedules-exclusions',
      category: 'Setup',
      field: 'Campaign Schedules exclude selected brand / market',
      rule: 'Campaign schedules should not include selected brand / market exclusions.',
      status: statusFromCheck(
        !hasCampaignExclusionsError &&
          !!input.exclusions &&
          schedulesExcludeSelections(input.campaignSchedules, excludedBrands, excludedCountries),
      ),
      statusLabel: 'Campaign Schedules exclude selected brand / market',
      summary: getCampaignScheduleSummary(
        hasCampaignExclusionsError,
        !!input.exclusions,
        input.campaignSchedules?.length ?? 0,
      ),
      evidence: [
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.excludedBrands,
          ...formatEvidence(excludedBrands.length ? excludedBrands.join(', ') : '-', sourceErrors.opportunity),
        },
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.excludedCountries,
          ...formatEvidence(excludedCountries.length ? excludedCountries.join(', ') : '-', sourceErrors.opportunity),
        },
        { label: SETUP_EVIDENCE_LABELS.loadedScheduleRows, value: String(input.campaignSchedules?.length ?? 0) },
      ],
    },
    {
      id: 'setup-marketing-terms',
      category: 'Setup',
      field: 'Marketing terms',
      rule: 'Auto-approve if marketing terms are consistent with brand.',
      status: statusFromCheck(
        !hasMarketingTermsError && valuesMatch(input.chainName, input.account?.contractTemplateId),
      ),
      statusLabel: 'Marketing terms',
      summary: hasMarketingTermsError
        ? 'Required chain or account evidence could not be loaded.'
        : 'Property chain from svc-reservation was compared with the account contract template evidence.',
      evidence: [
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.canonicalPropertyChain,
          ...formatEvidence(input.chainName, sourceErrors.chain),
        },
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.contractTemplateId,
          ...formatEvidence(input.account?.contractTemplateId, sourceErrors.account),
        },
      ],
    },
    {
      id: 'setup-contracts-geo',
      category: 'Setup',
      field: 'Geo',
      rule: 'Auto-approve if geo matches contract template.',
      status: statusFromCheck(!hasGeoError && valuesMatch(input.opportunity?.geo, input.account?.contractTemplateId)),
      statusLabel: 'Geo',
      summary: hasGeoError
        ? 'Required opportunity or account evidence could not be loaded.'
        : 'LE Contracts Geo was compared with the account contract template evidence.',
      evidence: [
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.leContractsGeo,
          ...formatEvidence(input.opportunity?.geo, sourceErrors.opportunity),
        },
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.contractTemplateId,
          ...formatEvidence(input.account?.contractTemplateId, sourceErrors.account),
        },
      ],
    },
    {
      id: 'setup-sell-cost-reporting-currency',
      category: 'Setup',
      field: 'Sell / cost / reporting currency',
      rule: 'Auto-approve if sell, cost, and reporting currency are the same.',
      status: statusFromCheck(
        !hasCurrencyError &&
          hasValue(sellCurrency) &&
          hasValue(costCurrency) &&
          hasValue(reportingCurrency) &&
          uniqueValues([sellCurrency, costCurrency, reportingCurrency, ...packageCurrencies]).length === 1,
      ),
      statusLabel: 'Sell / cost / reporting currency',
      summary: hasCurrencyError
        ? 'Required opportunity or account evidence could not be loaded.'
        : 'Sell, cost, reporting, and loaded package currencies were compared.',
      evidence: [
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.sellCurrency,
          ...formatEvidence(sellCurrency, sourceErrors.opportunity),
        },
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.costCurrency,
          ...formatEvidence(costCurrency, sourceErrors.opportunity),
        },
        {
          canOverride: true,
          label: SETUP_EVIDENCE_LABELS.reportingCurrency,
          ...formatEvidence(reportingCurrency, sourceErrors.account),
        },
        {
          label: SETUP_EVIDENCE_LABELS.packageCurrencies,
          value: packageCurrencies.length ? packageCurrencies.join(', ').toUpperCase() : '-',
        },
      ],
    },
  ];
}
